import { capture } from '@afterpaytouch/integrations/monitoring'
import { ErrorCode, PortalApiError } from '@afterpaytouch/portal-api/types'
import { createCheckoutStartIdentityErrorHandler } from './identityErrorHandler'
import { createCheckoutStartRecoverableTerminalErrorHandler } from './recoverableTerminalErrorHandler'
import { createCheckoutStartAURegulationErrorHandler } from './auRegulationErrorHandler'
import { createCheckoutStartAffordabilityErrorHandler } from './affordabilityErrorHandler'
import { createCheckoutStartProfileErrorHandler } from './profileErrorHandler'
import { createCheckoutStartComplianceErrorHandler } from './complianceErrorHandler'
import { createCheckoutStartConvergenceIneligibleErrorHandler } from './convergenceIneligibleErrorHandler'
import { createCheckoutStartDefaultTerminalErrorHandler } from './defaultTerminalErrorHandler'
import { ErrorHandler, CheckoutErrorContext } from './types'
import { Route } from '../../config/router'

type ErrorHandlerFactory = () => ErrorHandler

export const createCheckoutErrorHandler = (
  context: CheckoutErrorContext
): {
  handleError: (error: PortalApiError, errorCode: ErrorCode) => Promise<Route>
} => {
  const handlers: ErrorHandlerFactory[] = [
    createCheckoutStartConvergenceIneligibleErrorHandler,
    createCheckoutStartIdentityErrorHandler,
    createCheckoutStartRecoverableTerminalErrorHandler,
    createCheckoutStartAURegulationErrorHandler,
    createCheckoutStartAffordabilityErrorHandler,
    createCheckoutStartProfileErrorHandler,
    createCheckoutStartComplianceErrorHandler,
  ]

  const defaultHandler = createCheckoutStartDefaultTerminalErrorHandler()

  return {
    async handleError(error: PortalApiError, errorCode: ErrorCode): Promise<Route> {
      if (error instanceof Error) {
        capture.error(error)
      }

      for (const handler of handlers) {
        const currentHandler = handler()
        if (currentHandler.canHandle(errorCode, error)) {
          return (await currentHandler.handle(errorCode, error, context)) as Route
        }
      }

      return (await defaultHandler.handle(errorCode, error, context)) as Route
    },
  }
}

export const createErrorHandler = (context: CheckoutErrorContext): ReturnType<typeof createCheckoutErrorHandler> => {
  return createCheckoutErrorHandler(context)
}

export type { CheckoutErrorContext, ErrorHandler } from './types'

export {
  createCheckoutStartConvergenceIneligibleErrorHandler,
  createCheckoutStartIdentityErrorHandler,
  createCheckoutStartRecoverableTerminalErrorHandler,
  createCheckoutStartAURegulationErrorHandler,
  createCheckoutStartAffordabilityErrorHandler,
  createCheckoutStartProfileErrorHandler,
  createCheckoutStartComplianceErrorHandler,
  createCheckoutStartDefaultTerminalErrorHandler,
}
